/**
 * Iter98: Toggle-Karte für Browser-Push-Notifications.
 * Erscheint in Settings + Billing-Page.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Bell, BellOff, Send, AlertTriangle, Check } from "lucide-react";
import { toast } from "sonner";
import {
  disableWebPush,
  enableWebPush,
  getCurrentPushPermission,
  getCurrentPushSubscription,
  isPushSupported,
  sendTestPush,
} from "@/lib/webPush";

export default function PushSettingsCard() {
  const [supported, setSupported] = useState(null);
  const [permission, setPermission] = useState("default");
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    const s = await isPushSupported();
    setSupported(s);
    if (!s) return;
    setPermission(await getCurrentPushPermission());
    setSubscribed(!!(await getCurrentPushSubscription()));
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handleToggle = async (checked) => {
    setBusy(true);
    try {
      if (checked) {
        await enableWebPush();
        toast.success("Browser-Push aktiviert ✓");
      } else {
        await disableWebPush();
        toast.success("Browser-Push deaktiviert");
      }
      await refresh();
    } catch (e) {
      console.error(e);
      toast.error(e.message || "Fehler beim Aktivieren");
    } finally {
      setBusy(false);
    }
  };

  const handleTest = async () => {
    try {
      await sendTestPush();
      toast.success("Test-Push gesendet — schau in den Browser ✉️");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test fehlgeschlagen");
    }
  };

  if (supported === false) {
    return (
      <Card className="p-4 border-amber-200 bg-amber-50" data-testid="push-settings-unsupported">
        <div className="flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-700 mt-0.5" />
          <div className="text-xs text-amber-900">
            <span className="font-semibold">Browser-Push nicht unterstützt</span> in diesem Browser.
            iOS Safari erfordert „Zum Home-Bildschirm hinzufügen". Chrome/Firefox/Edge funktionieren.
          </div>
        </div>
      </Card>
    );
  }

  const denied = permission === "denied";

  return (
    <Card className="p-5 border-slate-200" data-testid="push-settings-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <div className={`h-9 w-9 rounded-md flex items-center justify-center flex-shrink-0 ${
            subscribed ? "bg-emerald-100" : "bg-slate-100"
          }`}>
            {subscribed ? (
              <Bell className="h-5 w-5 text-emerald-700" />
            ) : (
              <BellOff className="h-5 w-5 text-slate-500" />
            )}
          </div>
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-slate-900">Browser-Push-Notifications</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Erhalte Warnungen direkt im Browser — auch wenn die App-Tab geschlossen ist.
              Plan-Limits, Match-Vorschläge & wichtige Hinweise.
            </p>
            {subscribed && (
              <div className="mt-1 text-[11px] text-emerald-700 inline-flex items-center gap-1">
                <Check className="h-3 w-3" /> Aktiv auf diesem Gerät
              </div>
            )}
            {denied && (
              <div className="mt-1 text-[11px] text-rose-700">
                ⚠️ Browser-Berechtigung verweigert. Bitte über Browser-Settings freigeben.
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {subscribed && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleTest}
              data-testid="push-settings-test-btn"
            >
              <Send className="h-3.5 w-3.5 mr-1.5" />
              Test senden
            </Button>
          )}
          <Switch
            checked={subscribed}
            onCheckedChange={handleToggle}
            disabled={busy || denied || !supported}
            data-testid="push-settings-toggle"
          />
        </div>
      </div>
    </Card>
  );
}
